import React, { useState } from 'react';
import {
  DollarSign,
  TrendingDown,
  PieChart,
  BarChart3,
  ChevronDown,
  ChevronUp,
  Clock,
  Wallet,
  CheckCircle,
  AlertCircle
} from 'lucide-react';
import { formatCurrency, getRegistrationFeeLabel } from '../../../utils/helpers';

const SummaryReport = ({
  summary,
  filteredExpenses,
  formatCurrency: formatCurrencyProp,
  clubName
}) => {
  const [expandedSections, setExpandedSections] = useState({});

  const format = formatCurrencyProp || formatCurrency;

  const toggleSection = (section) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  return (
    <div className="space-y-6">
      {/* Summary Cards - PROPER FINANCIAL TERMINOLOGY */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">

        {/* 1. EXPECTED TOTAL - What players OWE the club */}
        <div className="bg-white rounded-xl shadow-sm border border-blue-200 p-6 bg-gradient-to-br from-blue-50 to-white">
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-sm font-medium text-blue-700">Expected Total</h3>
            <DollarSign className="text-blue-600" size={20} />
          </div>
          <p className="text-2xl font-bold text-blue-700">
            {format(summary.totalExpectedReceivables)}
          </p>
          <p className="text-xs text-blue-600 mt-1">
            {summary.registrationFeeCount} {getRegistrationFeeLabel({ name: clubName })}{summary.registrationFeeCount !== 1 ? 's' : ''} assigned
          </p>
          <p className="text-xs text-gray-500 mt-2 border-t border-blue-100 pt-2">
            💡 Total money to be collected
          </p>
        </div>

        {/* 2. RECEIVED - What has been PAID */}
        <div className="bg-white rounded-xl shadow-sm border border-green-200 p-6 bg-gradient-to-br from-green-50 to-white">
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-sm font-medium text-green-700">Received</h3>
            <CheckCircle className="text-green-600" size={20} />
          </div>
          <p className="text-2xl font-bold text-green-700">
            {format(summary.totalCollected)}
          </p>
          <p className="text-xs text-green-600 mt-1">
            {summary.paymentsReceivedCount} payment{summary.paymentsReceivedCount !== 1 ? 's' : ''} received
          </p>
          <p className="text-xs text-gray-500 mt-2 border-t border-green-100 pt-2">
            💡 Money actually received from players
          </p>
        </div>

        {/* 3. RECEIVABLES - What is still OWED */}
        <div className={`bg-white rounded-xl shadow-sm border p-6 ${summary.totalReceivables > 0
          ? 'border-amber-200 bg-gradient-to-br from-amber-50 to-white'
          : 'border-green-200 bg-gradient-to-br from-green-50 to-white'
          }`}>
          <div className="flex items-center justify-between mb-2">
            <h3 className={`text-sm font-medium ${summary.totalReceivables > 0 ? 'text-amber-700' : 'text-green-700'}`}>
              Receivables
            </h3>
            <Clock className={summary.totalReceivables > 0 ? 'text-amber-600' : 'text-green-600'} size={20} />
          </div>
          <p className={`text-2xl font-bold ${summary.totalReceivables > 0 ? 'text-amber-700' : 'text-green-700'}`}>
            {format(summary.totalReceivables)}
          </p>
          <p className={`text-xs mt-1 ${summary.totalReceivables > 0 ? 'text-amber-600' : 'text-green-600'}`}>
            {summary.totalReceivables > 0 ? 'Still owed by players' : 'All dues collected! ✓'}
          </p>
          <p className="text-xs text-gray-500 mt-2 border-t border-gray-100 pt-2">
            💡 Expected Total minus Received
          </p>
        </div>

        {/* 4. EXPENSES - What the club SPENT */}
        <div className="bg-white rounded-xl shadow-sm border border-red-200 p-6 bg-gradient-to-br from-red-50 to-white">
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-sm font-medium text-red-700">Expenses</h3>
            <TrendingDown className="text-red-600" size={20} />
          </div>
          <p className="text-2xl font-bold text-red-700">
            {format(summary.totalExpenses)}
          </p>
          <p className="text-xs text-red-600 mt-1">
            {filteredExpenses.filter(e => !e.isRegistrationFee).length} expense{filteredExpenses.filter(e => !e.isRegistrationFee).length !== 1 ? 's' : ''}
          </p>
          <p className="text-xs text-gray-500 mt-2 border-t border-red-100 pt-2">
            💡 Money spent by the club
          </p>
        </div>

        {/* 5. CASH POSITION - Actual money the club has */}
        <div className={`bg-white rounded-xl shadow-sm border p-6 ${summary.cashPosition >= 0
          ? 'border-green-200 bg-gradient-to-br from-green-50 to-white'
          : 'border-red-200 bg-gradient-to-br from-red-50 to-white'
          }`}>
          <div className="flex items-center justify-between mb-2">
            <h3 className={`text-sm font-medium ${summary.cashPosition >= 0 ? 'text-green-700' : 'text-red-700'}`}>
              Cash Position
            </h3>
            <Wallet className={summary.cashPosition >= 0 ? 'text-green-600' : 'text-red-600'} size={20} />
          </div>
          <p className={`text-2xl font-bold ${summary.cashPosition >= 0 ? 'text-green-700' : 'text-red-700'}`}>
            {format(Math.abs(summary.cashPosition))}
          </p>
          <p className={`text-xs mt-1 ${summary.cashPosition >= 0 ? 'text-green-600' : 'text-red-600'}`}>
            {summary.cashPosition >= 0 ? 'Available balance' : 'In deficit'}
          </p>
          <p className="text-xs text-gray-500 mt-2 border-t border-gray-100 pt-2">
            💡 Received minus Expenses
          </p>
        </div>
      </div>

      {/* Pending Expenses Alert */}
      {summary.pendingCount > 0 && (
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <AlertCircle className="text-amber-600" size={24} />
            <div>
              <p className="font-semibold text-amber-800">
                {summary.pendingCount} expense{summary.pendingCount !== 1 ? 's' : ''} pending approval
              </p>
              <p className="text-sm text-amber-700">
                Total: {format(summary.totalPending)}
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Expense by Category */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <div
          className="flex items-center justify-between cursor-pointer"
          onClick={() => toggleSection('category')}
        >
          <h3 className="text-lg font-bold text-gray-900 flex items-center gap-2">
            <PieChart size={20} className="text-green-600" />
            Expenses by Category
          </h3>
          {expandedSections['category'] ? <ChevronUp /> : <ChevronDown />}
        </div>

        {expandedSections['category'] && (
          <div className="mt-4 space-y-3">
            {Object.entries(summary.byCategory)
              .sort(([, a], [, b]) => b - a)
              .map(([category, amount]) => {
                const percentage = (amount / summary.totalExpenses * 100).toFixed(1);
                return (
                  <div key={category} className="flex items-center gap-4">
                    <div className="flex-1">
                      <div className="flex justify-between mb-1">
                        <span className="text-sm font-medium text-gray-700">{category}</span>
                        <span className="text-sm font-semibold text-gray-900">
                          {format(amount)} ({percentage}%)
                        </span>
                      </div>
                      <div className="w-full bg-gray-200 rounded-full h-2">
                        <div
                          className="bg-green-600 h-2 rounded-full"
                          style={{ width: `${percentage}%` }}
                        />
                      </div>
                    </div>
                  </div>
                );
              })}
          </div>
        )}
      </div>

      {/* Expense by Game */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <div
          className="flex items-center justify-between cursor-pointer"
          onClick={() => toggleSection('game')}
        >
          <h3 className="text-lg font-bold text-gray-900 flex items-center gap-2">
            <BarChart3 size={20} className="text-blue-600" />
            Expenses by Game
          </h3>
          {expandedSections['game'] ? <ChevronUp /> : <ChevronDown />}
        </div>

        {expandedSections['game'] && (
          <div className="mt-4">
            <div className="mb-4">
              <p className="text-sm text-gray-600">
                Average per game: <span className="font-semibold text-gray-900">{format(summary.avgExpensePerGame)}</span>
              </p>
            </div>
            <div className="space-y-2">
              {Object.entries(summary.byGame)
                .sort(([, a], [, b]) => b - a)
                .map(([gameName, amount]) => (
                  <div key={gameName} className="flex justify-between items-center py-2 border-b border-gray-100">
                    <span className="text-sm text-gray-700">{gameName}</span>
                    <span className="text-sm font-semibold text-gray-900">{format(amount)}</span>
                  </div>
                ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default SummaryReport;